import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import AvatarText from "@/components/reusables/AvatarText";
import FormDialog from "@/components/reusables/FormDialog";
import { SelectMulti } from "@/components/reusables/custom-form-fields/FormSelect";
import EmptyData from "@/components/state-pages/EmptyData";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Table from "@/components/ui/table";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { IReturnRequestItem, ProductType } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import { useApproveReturnRequestMutation } from "@/redux/api/toolsQuery";
import {
  TReturnRequestApproval,
  requestReturnApproveSchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

const CustomTableHeader = (): JSX.Element => {
  const commonClasses = "capitalize  border-r border-r-white text-primary ";

  return (
    <Table.TableHeader className={"bg-[#E4E8F2]"}>
      <Table.TableRow>
        <Table.TableHead className={cn(commonClasses)}>Name</Table.TableHead>
        <Table.TableHead className={commonClasses}>Quantity</Table.TableHead>
        <Table.TableHead className={commonClasses}>
          Sub Products
        </Table.TableHead>
      </Table.TableRow>
    </Table.TableHeader>
  );
};

interface ApproveReturnRequestModalProps {
  isOpen: boolean;
  data: IReturnRequestItem[];
  toggleModal: () => void;
}

const ApproveReturnRequestModal = ({
  isOpen,
  data,
  toggleModal,
}: ApproveReturnRequestModalProps): JSX.Element => {
  const { id, reset } = useQueryID(ToolsKeys.RETURN_REQUEST_ID);
  const {
    FORM: { RETURN_REQUEST },
    REQUEST,
  } = ToolsConstants;

  const [approveReturnRequest, { isLoading }] =
    useApproveReturnRequestMutation();

  const form = useForm<TReturnRequestApproval>({
    resolver: zodResolver(requestReturnApproveSchema),
    values: {
      items: data?.length
        ? data?.map((item) => ({
            returnItemId: item?.id,
            productId: item?.productId,
            productAssignmentId: item?.productAssignmentId,
            quantity: item?.quantity || 0,
            type: item?.product?.type,
            subProductIds:
              item?.subProducts?.map((subProduct) => subProduct.id) || [],
          }))
        : [],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "items",
    keyName: "idx",
  });

  const handleExit = (): void => {
    reset();
    toggleModal();
  };

  const onSubmit = async (data: TReturnRequestApproval): Promise<void> => {
    const response = await approveReturnRequest({
      body: data?.items,
      id,
    });

    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(RETURN_REQUEST.TOAST_SUCCESS);
        handleExit();
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      toggleModal={handleExit}
      title={RETURN_REQUEST.APPROVE_REQUEST}
      className='md:max-w-4xl'
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <ScrollArea className='h-[400px] sidebar_scrollbar'>
            <div className='rounded-md border overflow-clip'>
              <Table.Table>
                <CustomTableHeader />
                <Table.TableBody>
                  {fields.length ? (
                    fields?.map((field, index) => {
                      const commonCellClassName = "border-r border-r-gray-100";

                      const mappedItem = data?.find(
                        (item) =>
                          item?.productAssignmentId ===
                          field?.productAssignmentId,
                      );

                      return (
                        <Table.TableRow
                          key={field.idx}
                          className='even:bg-transparent hover:bg-transparent'
                        >
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <AvatarText
                              src={mappedItem?.product?.images?.[0]}
                              name={mappedItem?.product?.name || ""}
                              title={mappedItem?.product?.name || ""}
                              classNames={{
                                titleClassName: "line-clamp-1 text-wrap",
                              }}
                            />
                          </Table.TableCell>
                          <Table.TableCell className={cn(commonCellClassName)}>
                            <FormInput
                              name={`items.${index}.quantity`}
                              form={form}
                              input={{
                                placeholder: "Enter quantity",
                                type: "number",
                              }}
                            />
                          </Table.TableCell>
                          <Table.TableCell
                            className={cn(
                              commonCellClassName,
                              "min-w-72 max-w-72",
                            )}
                          >
                            {mappedItem?.product?.type ===
                            ProductType.Has_Quantity ? (
                              <div className='pl-3 '>--</div>
                            ) : (
                              <>
                                <SelectMulti
                                  className='mt-2'
                                  maxSelection={
                                    form.watch(`items.${index}.quantity`) ??
                                    undefined
                                  }
                                  withoutForm={{
                                    values:
                                      form.getValues(
                                        `items.${index}.subProductIds`,
                                      ) ?? [],
                                    onValueChange: (values) => {
                                      const selectedIds = values.map(
                                        (value) => value.value,
                                      );
                                      form.setValue(
                                        `items.${index}.subProductIds`,
                                        selectedIds,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    },
                                  }}
                                  options={
                                    mappedItem?.subProducts?.length
                                      ? mappedItem.subProducts.map(
                                          (subProduct) => ({
                                            label: subProduct?.name,
                                            value: subProduct?.id,
                                          }),
                                        )
                                      : []
                                  }
                                  isRequired
                                />
                                <span className='text-destructive text-xs'>
                                  {
                                    form.formState.errors?.items?.[index]
                                      ?.subProductIds?.message
                                  }
                                </span>
                              </>
                            )}
                          </Table.TableCell>
                        </Table.TableRow>
                      );
                    })
                  ) : (
                    <Table.TableRow>
                      <Table.TableCell colSpan={3}>
                        <EmptyData
                          title={REQUEST.RETURN_REQUEST.TITLE}
                          subtitle={
                            REQUEST.RETURN_REQUEST.NO_RETURN_REQUEST_PRODUCTS
                          }
                        />
                      </Table.TableCell>
                    </Table.TableRow>
                  )}
                </Table.TableBody>
              </Table.Table>
            </div>
          </ScrollArea>
          <div className=' flex justify-between '>
            <Button
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isLoading}
            >
              {Button_Names.Reset}
            </Button>
            <Button className='w-[300px]' type='submit' isLoading={isLoading}>
              {RETURN_REQUEST.APPROVE_REQUEST}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default ApproveReturnRequestModal;
